import React, { useCallback, useEffect, useMemo, useRef } from "react";
import { localize } from "src/l10n";
import "./TimelineBoards.scss";
import { ActionMenu, Label, UnstyledButton } from "src/ui";
import Visage2Icon from "src/visage2/Visage2Icon/Visage2Icon";
import { CreatePlannerItemHandler, IPlannerBoardSetting, PlannerPositioning, PlannerRights, PlannerTimelineGroup, UpdateCategoryHandler, UpdateCategoryTypeHandler } from "src/components/Planner/types";
import { useDispatch } from "react-redux";
import { plannerBoardDeleted } from "src/redux";
import { setConfirmPopup } from "src/popups/actions";
import { deletePlannerBoardAsync } from "src/api/plannerApi";
import { SpintrTypes } from "src/typings";
import { plannerStyling } from "src/components/Planner/utils";
import { SmallHeading } from "src/components/Text";
import { ReactSVG } from "react-svg";
import { PrimaryButton, TextField } from "@fluentui/react";
import moment from "moment";

interface IProps {
    headerHeight: number;
    data: PlannerTimelineGroup[];
    positioning: PlannerPositioning;
    toggleBoardExpanded: (id: number) => void;
    displayBoardForm: (id?: number) => void;
    onCreateClick?: CreatePlannerItemHandler | undefined;
    reportHeight: (height: number) => void;
    isSmallViewMode: boolean;
    isLoading: boolean;
    onCategoryUpdated: UpdateCategoryHandler;
    onCategoryTypeUpdated: UpdateCategoryTypeHandler;
    rights: PlannerRights;
    settings: IPlannerBoardSetting[];
    boardId?: number;
}

interface IState {
    isEditingCategory: boolean;
    isEditingCategoryType: boolean;
    editingBoardId: number;
    editingCategoryId: string;
    editingCategoryTypeId: string;
    editingName: string;
}

const initialState: IState = {
    isEditingCategory: false,
    isEditingCategoryType: false,
    editingBoardId: 0,
    editingCategoryId: "",
    editingCategoryTypeId: "",
    editingName: "",
}

const TimelineBoards = (props: IProps) => {
    const dispatch = useDispatch();
    const ref = useRef<HTMLDivElement>();

    const [state, setState] = React.useState<IState>(initialState);

    const onEditClick = useCallback((boardId: number, categoryId: string, categoryTypeId: string, name: string, type: "category" | "categoryType") => {
        setState((prevState) => ({
            ...prevState,
            isEditingCategory: type === "category",
            isEditingCategoryType: type === "categoryType",
            editingBoardId: boardId,
            editingCategoryId: categoryId,
            editingCategoryTypeId: categoryTypeId,
            editingName: name,
        }));
    }, [dispatch]);

    const onSave = useCallback(() => {
        if (state.isEditingCategory) {
            props.onCategoryUpdated({
                id: state.editingCategoryId,
                name: state.editingName,
                contentStatus: SpintrTypes.ContentStatus.Published,
            });
        } else if (state.isEditingCategoryType) {
            props.onCategoryTypeUpdated({
                id: state.editingCategoryTypeId,
                name: state.editingName,
                contentStatus: SpintrTypes.ContentStatus.Published,
            });
        }

        setState((prevState) => ({
            ...prevState,
            ...initialState
        }));
    }, [dispatch, state.editingCategoryId, state.editingCategoryTypeId, state.editingName, state.isEditingCategory, state.isEditingCategoryType]);

    useEffect(() => {
        props.reportHeight(ref.current.clientHeight);
    }, [props.data, ref]);

    const enableCustomBoards = useMemo(() => props.settings.find(s => s.type === SpintrTypes.PlannerBoardType.Custom)?.isEnabled, [props.settings]);

    return (
        <div ref={ref} className="TimelineBoards">
            <div className="header" style={{
                height: props.headerHeight
            }}>
                <span>{localize("Planering")}</span>
                {!props.boardId && enableCustomBoards && (
                    <UnstyledButton className="create-board-button" onClick={() => props.displayBoardForm()}>
                        <Visage2Icon icon="add" />
                    </UnstyledButton>
                )}
            </div>
            {!props.isLoading && (
                <div className="boards">
                    {props.data.length > 0 && (
                        <div className="left-border-box-wrapper" style={{ height: "calc(100% - " + props.headerHeight + "px)" }}>
                            <div className="left-border-box" style={{ backgroundColor: props.data[props.data.length - 1].color }} />
                        </div>
                    )}
                    {props.data.map((b) => {
                        const canEditBoard = props.rights.editableBoardIds.includes(Number(b.key));

                        return (
                            <div
                                className="board"
                                style={{
                                    top: props.positioning[b.key].topOffset,
                                    height: props.positioning[b.key].height,
                                }}
                                key={b.key}
                            >
                                <div className="left-border-box-wrapper" style={{ height: props.positioning[b.key].height }}>
                                    <div className="left-border-box" style={{ backgroundColor: b.color }} />
                                </div>
                                <div className="board-color-rectangle">
                                    <ReactSVG
                                        src={props.isSmallViewMode ? "/images/planner-rectangle-small.svg" : "/images/planner-rectangle.svg"}
                                        beforeInjection={(svg) => {
                                            const stops = svg.querySelectorAll("stop");

                                            stops[0].setAttribute("stop-color", b.color);
                                            stops[1].setAttribute("stop-color", "#FFFFFF");
                                        }}
                                    />
                                </div>
                                <div className="board-title-row">
                                    <div className="row-left">
                                        <div className="board-title">
                                            <SmallHeading title={b.name}>{b.name}</SmallHeading>
                                            <div className="board-actions">
                                                {b.type === SpintrTypes.PlannerBoardType.Custom && canEditBoard && (
                                                    <ActionMenu
                                                        categories={[{
                                                            items: [{
                                                                text: localize("Redigera"),
                                                                onClick: () => props.displayBoardForm(Number(b.key))
                                                            }, {
                                                                text: localize("TaBort"),
                                                                onClick: () => {
                                                                    dispatch(setConfirmPopup({
                                                                        isOpen: true,
                                                                        title: localize("RaderaInnehall"),
                                                                        message: localize("ArDuSakerPaAttDuVillX").replace("{{X}}", localize("RaderaDetta").toLowerCase()),
                                                                        onConfirm: () => {
                                                                            dispatch(async () => {
                                                                                await deletePlannerBoardAsync(Number(b.key));
                                                                            });
                                                                            dispatch(plannerBoardDeleted(Number(b.key)));
                                                                        },
                                                                    }));
                                                                },
                                                            }],
                                                        }]}
                                                        className="options-button"
                                                    />
                                                )}
                                                <UnstyledButton className="chevron" onClick={() => props.toggleBoardExpanded(Number(b.key))}>
                                                    <Visage2Icon icon={b.expanded ? "arrow-down-1" : "arrow-up-2"} />
                                                </UnstyledButton>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="board-title-right"></div>
                                </div>
                                {!!b.expanded && b.categories.map((c) => {
                                    const isEditing = (state.isEditingCategory || state.isEditingCategoryType) &&
                                        (state.editingBoardId === Number(b.key) && state.editingCategoryId === c.categoryId  && state.editingCategoryTypeId === c.categoryTypeId);

                                    const categoryName = c.isLangTag ? localize(c.name) : c.name;

                                    return(
                                        <div
                                            className="category"
                                            style={{
                                                top: props.positioning[b.key].nodes[c.key].topOffset,
                                                height: props.positioning[b.key].nodes[c.key].height
                                            }}
                                            key={c.key}
                                        >
                                            <div className="row-left" style={{
                                                height: plannerStyling.categoryRowHeight
                                            }}>
                                                {isEditing && (
                                                    <form onSubmit={(ev) => {
                                                        ev.preventDefault();
                                                        onSave()
                                                    }}>
                                                        <div className="category-editing">
                                                                <TextField
                                                                    value={state.editingName}
                                                                    autoFocus
                                                                    borderless
                                                                    onChange={(_, newValue) => setState((prevState) => ({
                                                                        ...prevState,
                                                                        editingName: newValue
                                                                    }))}
                                                                />
                                                                <PrimaryButton
                                                                    text={localize("Spara")}
                                                                    onClick={onSave}
                                                                />
                                                        </div>
                                                    </form>
                                                )}
                                                {!isEditing && (
                                                    <div className="category-title">
                                                        {!c.isPlaceholder && (
                                                            <div>
                                                                {c.categoryTypeName && <Label size="body-2" color="primaryContent">{c.categoryTypeName}</Label>}
                                                                <Label weight="medium" color="dark-grey">{categoryName}</Label>
                                                            </div>
                                                        )}
                                                        {c.isPlaceholder && (
                                                            <div className="empty-create-categories-button">
                                                                {canEditBoard && (
                                                                    <UnstyledButton
                                                                        onClick={() => props.onCreateClick(Number(b.key), "", "", moment().toDate(), moment().add(1, "day").toDate())}
                                                                    >
                                                                        <Visage2Icon icon="add-circle" color="light-blue" />
                                                                        <Label color="light-blue" weight="medium">{localize("SkapaNy")}</Label>
                                                                    </UnstyledButton>
                                                                )}
                                                                {!canEditBoard && (
                                                                    <Label>{localize("Tomt")}</Label>
                                                                )}
                                                            </div>
                                                        )}
                                                        {canEditBoard && !c.isReadOnly && !c.isPlaceholder && (
                                                            <ActionMenu
                                                                categories={[{
                                                                    items: [{
                                                                        text: localize("EDIT_CATEGORY"),
                                                                        onClick: () => onEditClick(Number(b.key), c.categoryId, c.categoryTypeId, c.name, "category"),
                                                                    }, ...(c.categoryTypeId ? [{
                                                                        text: localize("EDIT_CATEGORY_TYPE"),
                                                                        onClick: () => onEditClick(Number(b.key), c.categoryId, c.categoryTypeId, c.categoryTypeName, "categoryType"),
                                                                    }] : [])],
                                                                }]}
                                                                className="options-button"
                                                            />
                                                        )}
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                    )
                                })}
                            </div>
                        )
                    }
                )}
                </div>
            )}
        </div>
    )
}

export default TimelineBoards;
